class AuthForm {
	protected form: HTMLFormElement;
	protected messageContainer: HTMLElement | null;

	constructor(formSelector: string) {
		const form = document.querySelector<HTMLFormElement>(formSelector);
		if (!form) throw new Error(`Form not found: ${formSelector}`);

		this.form = form;
		this.messageContainer = form.querySelector('.form-messages');

		this.form.addEventListener('submit', this.handleSubmit.bind(this));
		this.initializePasswordReveal();
	}

	protected async handleSubmit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!(await this.preSubmitValidation())) {
			return; // Stop if validation fails (e.g., Turnstile)
		}
		const formData = new FormData(this.form);

		if (
			event.submitter &&
			event.submitter instanceof HTMLButtonElement &&
			event.submitter.type === 'submit'
		) {
			// Add the submitter's name and value to the FormData
			const submitterName = event.submitter.name;
			const submitterValue = event.submitter.value;

			if (submitterName) {
				formData.append(submitterName, submitterValue);
			}
		}
		const body = Object.fromEntries(formData.entries());
		const action = this.getFormAction();
		this.clearFieldErrors();
		try {
			const response = await fetch(`/wp-json/sageverse/v1/${action}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce': window.sageverseData.nonce,
				},
				body: JSON.stringify(body),
				credentials: 'include',
			});
			const result = await response.json();
			this.handleResponse(result, action);
		} catch (error) {
			this.setMessage('An error occurred. Please try again.');
		}
	}

	protected async preSubmitValidation(): Promise<boolean> {
		// Can be overridden by subclasses for extra validation (e.g., Turnstile)
		return true;
	}

	protected getFormAction(): string {
		if (this.form.classList.contains('form-user--login')) return 'login';
		if (this.form.classList.contains('form-user--register')) return 'register';
		if (this.form.classList.contains('form-user--reset-password')) return 'reset-password';
		if (this.form.classList.contains('form-user--settings')) return 'settings';
		if (this.form.classList.contains('form-user--interests')) return 'settings';
		if (this.form.classList.contains('form-user--logout')) return 'logout';
		if (this.form.classList.contains('form-user--two-factor')) return 'two-factor';
		if (this.form.classList.contains('form-user--update-email')) return 'update-email';
		if (this.form.classList.contains('form-user--delete-account')) return 'delete-account';
		throw new Error('Unknown form type');
	}

	protected handleResponse(result: any, action: string): void {
		if (result.invalid) {
			this.clearFieldErrors();

			// If invalid is an object, show messages for each field. Otherwise just mark fields as invalid.
			if (Array.isArray(result.invalid)) {
				result.invalid.forEach((fieldName: string) => {
					const field = this.form.elements.namedItem(fieldName) as HTMLElement;
					if (field) {
						field.setAttribute('aria-invalid', 'true');
					}
				});
			} else {
				Object.entries(result.invalid).forEach(([fieldName, message]) => {
					const field = this.form.elements.namedItem(fieldName) as HTMLElement;
					if (field) {
						field.setAttribute('aria-invalid', 'true');

						const errorMessage = document.createElement('span');
						errorMessage.className = 'form-messages__error';
						errorMessage.textContent = message as string;

						const fieldset = field.closest('fieldset');

						if (fieldset) {
							fieldset.appendChild(errorMessage);
						}
					}
				});
			}
		}

		if (result.message) {
			this.setMessage(result.message, result.success);
		}
		if (result.success && result.redirect) {
			if (typeof result.redirect_delay === 'number') {
				setTimeout(() => {
					window.location.href = result.redirect;
				}, result.redirect_delay);
			} else {
				window.location.href = result.redirect;
			}
		} else {
			this.handleErrors(result, action);
		}
	}

	protected handleErrors(result: any, action: string): void {
		// Extendable error handling function.
	}

	protected clearFieldErrors(): void {
		this.form.querySelectorAll('[aria-invalid]').forEach((field) => {
			field.setAttribute('aria-invalid', 'false');
		});

		this.form.querySelectorAll('.form-messages__error').forEach((message) => {
			message.remove();
		});

		this.setMessage('', false);
	}

	protected setMessage(message: string, isSuccess = false): void {
		if (this.messageContainer) {
			// Clear any existing messages
			this.messageContainer.innerHTML = '';

			// Create new message element
			const messageElement = document.createElement('p');
			messageElement.className = isSuccess
				? 'form-messages__success'
				: 'form-messages__error';
			messageElement.textContent = message;

			// Add ARIA attributes for screen readers
			messageElement.setAttribute('role', isSuccess ? 'status' : 'alert');
			if (isSuccess) {
				messageElement.setAttribute('aria-label', 'Success message');
			} else {
				messageElement.setAttribute('aria-label', 'Error message');
			}

			this.messageContainer.appendChild(messageElement);
		}
	}

	protected initializePasswordReveal(): void {
		const passwordContainers = this.form.querySelectorAll('.password-container');

		passwordContainers.forEach((container) => {
			const toggleButton = container.querySelector('.password-reveal-toggle');
			const passwordInput =
				container.querySelector<HTMLInputElement>('input[type="password"]');
			const showIcon = toggleButton?.querySelector<HTMLElement>('.icon-show-password');
			const hideIcon = toggleButton?.querySelector<HTMLElement>('.icon-hide-password');

			if (!toggleButton || !passwordInput || !showIcon || !hideIcon) return;

			toggleButton.addEventListener('click', (e) => {
				e.preventDefault();
				const isRevealed = container.classList.contains('password-revealed');

				if (isRevealed) {
					container.classList.remove('password-revealed');
					passwordInput.type = 'password';
					toggleButton.setAttribute('aria-checked', 'false');
					toggleButton.setAttribute('aria-label', 'Show password');
					showIcon.style.display = 'none';
					hideIcon.style.display = 'flex';
				} else {
					container.classList.add('password-revealed');
					passwordInput.type = 'text';
					toggleButton.setAttribute('aria-checked', 'true');
					toggleButton.setAttribute('aria-label', 'Hide password');
					showIcon.style.display = 'flex';
					hideIcon.style.display = 'none';
				}
			});
		});
	}
}

export default AuthForm;
